import { ReactElement, ReactNode } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { IconDecimalFloat } from '@components/icons/decimal-float';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { useFormBuilderContext } from './context';
import { SmallDragHandle } from './drag-handle';
import { FieldEditHeader, FieldEditHeaderProps } from './field-edit-header';
import { getFieldConfigForKind } from './field-registry';
import { StaleConditionWarning } from './stale-condition-warning';
import { StateField } from './state';

export type FieldEditProps = {
  display: ReactNode;
  edit: ReactNode;
  menu?: FieldEditHeaderProps['menu'];
  field: StateField;
  dragHandle?: ReactElement;
};

export const FieldEdit = ({ display, edit, menu, field, dragHandle }: FieldEditProps) => {
  const context = useFormBuilderContext();
  const {
    store: { actions, useIsFieldSelected },
  } = context;
  const { t } = useScopedTranslate(locale);
  const isSelected = useIsFieldSelected(field.id);
  const animationProps = isSelected ? selectedAnimation : displayAnimation;
  const config = getFieldConfigForKind(field.kind);
  const IconToUse =
    field.kind === 'input-number' && field.isDecimal ? IconDecimalFloat : config.Icon;

  const disallowDrag = field.builderProps?.canReorder === false;

  const draggable = useDraggable({
    id: `selected-${field.id}`,
    data: {
      field,
    },
    disabled: disallowDrag,
  });

  if (draggable.isDragging) {
    return null;
  }

  const isTravellerField = 'companyAttributeId' in field && field.companyAttributeId !== undefined;

  return (
    <FieldEditContainer
      id={`field-${field.id}`}
      ref={draggable.setNodeRef}
      data-kind={field.kind}
      data-selected={isSelected}
      onClick={(e) => {
        e.stopPropagation();

        if (!isSelected) {
          actions.selectField(field.id);
        }
      }}
      {...(!isSelected && {
        ...draggable.attributes,
        ...draggable.listeners,
      })}
    >
      {isSelected && <ActiveBoxShadow />}
      <StaleConditionWarning field={field} />
      <AnimatePresence initial={false}>
        <motion.div
          key={isSelected ? `selected-${field.id}` : `display-${field.id}`}
          {...animationProps}
        >
          <>
            {isSelected ? (
              <Stack space={4}>
                <FieldEditHeader
                  field={field}
                  menu={menu}
                  content={
                    <FieldEditHeader.Title
                      dragHandle={
                        !disallowDrag && (
                          <SmallDragHandle {...draggable.listeners} {...draggable.attributes} />
                        )
                      }
                      label={config.title}
                      icon={IconToUse}
                    />
                  }
                />
                {isTravellerField && (
                  <Text text={t('TravellerField')} font="regular14" color="elementPurple50" />
                )}
                <Separator />
                {edit}
              </Stack>
            ) : (
              <div style={{ pointerEvents: 'none' }}>{display}</div>
            )}
          </>
        </motion.div>
      </AnimatePresence>
    </FieldEditContainer>
  );
};

const shadowWidth = 8;

const ActiveBoxShadow = styled.div({
  position: 'absolute',
  left: pxToRem(-shadowWidth),
  right: pxToRem(-shadowWidth),
  bottom: pxToRem(-shadowWidth),
  top: pxToRem(-shadowWidth),
  borderRadius: pxToRem(shadowWidth),
  border: `${pxToRem(shadowWidth)} solid ${theme.color.blueTrans20}`,
  zIndex: -1,
});

const selectedAnimation: AnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { type: 'just' } },
};

const displayAnimation: AnimationProps = {
  initial: { opacity: 0.5 },
  animate: { opacity: 1 },
};

export const FieldEditContainer = styled.div({
  cursor: 'pointer',
  background: 'transparent',
  border: `${pxToRem(1)} solid transparent`,
  borderRadius: pxToRem(4),
  padding: pxToRem(8),
  position: 'relative',
  transition: transitionStandard('background'),
  overflow: 'hidden',
  '&:active': {
    cursor: 'grab',
  },
  '&[data-selected="true"]': {
    borderWidth: pxToRem(2),
    padding: pxToRem(14),
    background: theme.color.backgroundLightDark,
    borderColor: theme.color.elementBlue100,
  },

  '&[data-selected="false"]:hover': {
    borderColor: theme.color.border10,
    background: theme.color.uiTrans5,
  },
  ol: {
    margin: 0,
    paddingInlineStart: 25,
  },

  ul: {
    paddingInlineStart: 25,
  },
});

const locale = {
  en: {
    TravellerField: 'Traveller profile field',
  },
};
